import React, { useState, useEffect } from 'react';
import { invoicesApi, patientsApi, invoiceItemsApi, assessmentsApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiX, HiPlus, HiTrash } from 'react-icons/hi';
import { usePermissions } from '../../hooks/usePermissions';
import { formatValidationErrors } from '../../utils/errorHandler';
import { todayYmdIST, formatDateForInputIST, formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const InvoiceForm = ({ invoice, patient: initialPatient, assessment: initialAssessment, onClose, onSuccess }) => {
  const { canCreate, canUpdate } = usePermissions();
  const lockedPatient = invoice ? null : (initialPatient || null);
  const lockedAssessment = invoice ? null : (initialAssessment || null);
  const [loading, setLoading] = useState(false);
  const [patients, setPatients] = useState([]);
  const [invoiceItemDescriptions, setInvoiceItemDescriptions] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [formData, setFormData] = useState({
    patient_id: lockedPatient?.id ? String(lockedPatient.id) : '',
    invoice_date: todayYmdIST(),
    due_date: '',
    invoice_category: lockedPatient ? 'patient' : (lockedAssessment ? 'assessment' : 'assessment'),
    payment_type: lockedPatient ? 'therapy' : 'assessment',
    assessment_id: lockedAssessment?.id ? String(lockedAssessment.id) : '',
    status: 'draft',
    tax_rate: '',
    discount_amount: '',
    notes: '',
    items: [{ description: '', quantity: 1, unit_price: '', showQuantity: false }]
  });

  useEffect(() => {
    fetchPatients();
    fetchInvoiceItemDescriptions();
    if (!invoice && !lockedPatient) {
      fetchAssessments();
    }
    if (!invoice && lockedAssessment?.id) {
      fetchAssessments();
    }
    if (invoice) {
      // Load invoice data for editing
      const isAssessmentInvoice = invoice.payment_type === 'assessment' && invoice.assessment_id;
      const subtotal = parseFloat(invoice.subtotal) || 0;
      const taxAmount = parseFloat(invoice.tax_amount) || 0;
      const derivedTaxRate = subtotal > 0 && taxAmount > 0
        ? (taxAmount / subtotal) * 100
        : null;
      setFormData({
        patient_id: invoice.patient_id != null ? String(invoice.patient_id) : '',
        invoice_date: formatDateForInputIST(invoice.invoice_date) || todayYmdIST(),
        due_date: formatDateForInputIST(invoice.due_date) || '',
        invoice_category: isAssessmentInvoice ? 'assessment' : 'patient',
        payment_type: invoice.payment_type || 'therapy',
        assessment_id: invoice.assessment_id != null ? String(invoice.assessment_id) : '',
        status: invoice.status || 'draft',
        tax_rate: invoice.tax_rate !== null && invoice.tax_rate !== undefined && parseFloat(invoice.tax_rate) > 0
          ? parseFloat(invoice.tax_rate)
          : (derivedTaxRate != null && derivedTaxRate > 0 ? derivedTaxRate : ''),
        discount_amount: invoice.discount_amount !== null && invoice.discount_amount !== undefined && parseFloat(invoice.discount_amount) > 0 ? parseFloat(invoice.discount_amount) : '',
        notes: invoice.notes || '',
        items: invoice.items && invoice.items.length > 0 
          ? invoice.items.map(item => ({
              description: item.description || '',
              quantity: parseFloat(item.quantity) || 1,
              unit_price: item.unit_price && parseFloat(item.unit_price) > 0 ? parseFloat(item.unit_price) : '',
              showQuantity: item.show_quantity != null
                ? !!item.show_quantity
                : !!(item.quantity && parseFloat(item.quantity) !== 1),
              therapy_plan_id: item.therapy_plan_id || null,
              period_from: item.period_from ? formatDateForInputIST(item.period_from) : null,
              period_to: item.period_to ? formatDateForInputIST(item.period_to) : null
            }))
          : [{ description: '', quantity: 1, unit_price: '', showQuantity: false }]
      });
      
      // Fetch assessments if editing an assessment invoice
      if (isAssessmentInvoice) {
        fetchAssessments(); // Fetch all assessments for assessment invoices
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  const fetchPatients = async () => {
    try {
      const response = await patientsApi.getAll({ limit: 1000 });
      setPatients(response.data.data.patients || []);
    } catch (error) {
      toast.error('Failed to fetch patients');
    }
  };

  const fetchInvoiceItemDescriptions = async () => {
    try {
      const response = await invoiceItemsApi.getAll({ is_active: true });
      setInvoiceItemDescriptions(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch invoice item descriptions:', error);
    }
  };

  const fetchAssessments = async (patientId = null) => {
    try {
      const params = { limit: 100 };
      if (patientId) {
        params.patient_id = patientId;
      }
      const response = await assessmentsApi.getAll(params);
      setAssessments(response.data.data.assessments || []);
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      setAssessments([]);
    }
  };


  const handleChange = (e) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'number' 
          ? (value === '' ? '' : (isNaN(parseFloat(value)) ? '' : parseFloat(value)))
          : value
      };

      // Handle invoice category change
      if (name === 'invoice_category') {
        if (value === 'assessment') {
          newData.payment_type = 'assessment';
          if (!lockedPatient) {
            newData.patient_id = '';
          }
          fetchAssessments();
        } else {
          newData.payment_type = 'therapy';
          newData.assessment_id = '';
          if (lockedPatient?.id) {
            newData.patient_id = String(lockedPatient.id);
          }
          setAssessments([]);
        }
      }

      // Handle assessment change - no need to fetch by patient
      if (name === 'assessment_id' && value) {
        // Optionally fetch assessment details to show patient info
        // But don't require patient selection
      }

      return newData;
    });
    calculateTotals();
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        [field]: field === 'quantity'
          ? value === '' ? '' : (parseFloat(value) || '')
          : field === 'unit_price'
          ? value === '' ? '' : parseFloat(value) || ''
          : value
      };

      // Auto-fill price when description is selected
      if (field === 'description' && value) {
        const selectedDesc = invoiceItemDescriptions.find(
          desc => desc.description === value
        );
        if (selectedDesc && selectedDesc.default_price !== null && selectedDesc.default_price !== undefined) {
          newItems[index].unit_price = parseFloat(selectedDesc.default_price);
        }
      }

      return { ...prev, items: newItems };
    });
    calculateTotals();
  };

  const toggleItemQuantity = (index) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = {
        ...newItems[index],
        showQuantity: !newItems[index].showQuantity,
        quantity: newItems[index].showQuantity ? 1 : newItems[index].quantity // Reset to 1 when hiding
      };
      return { ...prev, items: newItems };
    });
    calculateTotals();
  };

  const calculateTotals = () => {
    // This will be called after state updates
    setTimeout(() => {
      setFormData(prev => {
        const subtotal = prev.items.reduce((sum, item) => {
          const qty = item.showQuantity
            ? (parseFloat(item.quantity) || 0)
            : (parseFloat(item.quantity) || 1);
          return sum + qty * (parseFloat(item.unit_price) || 0);
        }, 0);
        const taxAmount = (subtotal * prev.tax_rate) / 100;
        const totalAmount = subtotal + taxAmount - prev.discount_amount;
        
        return { ...prev, subtotal, taxAmount, totalAmount };
      });
    }, 0);
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, unit_price: '', showQuantity: false }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
      calculateTotals();
    } else {
      toast.error('Invoice must have at least one item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check permissions
    if (invoice && !canUpdate('invoices')) {
      toast.error('You do not have permission to update invoices');
      return;
    }
    if (!invoice && !canCreate('invoices')) {
      toast.error('You do not have permission to create invoices');
      return;
    }

    // Validate form data
    if (formData.invoice_category === 'patient' && !formData.patient_id) {
      toast.error('Please select a patient for patient fees invoice');
      return;
    }

    if (formData.invoice_category === 'assessment' && !formData.assessment_id) {
      toast.error('Please select an assessment for assessment fees invoice');
      return;
    }

    setLoading(true);

    try {
      // Validate items
      const validItems = formData.items.filter(item => 
        item.description.trim() !== '' && item.quantity > 0 && item.unit_price !== '' && parseFloat(item.unit_price) > 0
      );

      if (validItems.length === 0) {
        toast.error('Please add at least one valid item');
        setLoading(false);
        return;
      }

      const submitData = {
        patient_id: formData.invoice_category === 'patient' && formData.patient_id ? parseInt(formData.patient_id) : null,
        invoice_date: formData.invoice_date,
        due_date: formData.due_date || null,
        payment_type: formData.payment_type,
        assessment_id: formData.invoice_category === 'assessment' && formData.assessment_id ? parseInt(formData.assessment_id) : null,
        status: formData.status,
        tax_rate: formData.tax_rate === '' ? 0 : (parseFloat(formData.tax_rate) || 0),
        discount_amount: formData.discount_amount === '' ? 0 : (parseFloat(formData.discount_amount) || 0),
        notes: formData.notes || null,
        items: validItems.map(item => ({
          description: item.description.trim(),
          quantity: parseFloat(item.quantity),
          unit_price: parseFloat(item.unit_price) || 0,
          show_quantity: item.showQuantity ? 1 : 0,
          therapy_plan_id: item.therapy_plan_id || null,
          period_from: item.period_from || null,
          period_to: item.period_to || null
        }))
      };

      if (invoice) {
        await invoicesApi.update(invoice.id, submitData);
        toast.success('Invoice updated successfully');
      } else {
        await invoicesApi.create(submitData);
        toast.success('Invoice created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(formatValidationErrors(error));
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for display
  const subtotal = formData.items.reduce((sum, item) => {
    const qty = item.showQuantity
      ? (parseFloat(item.quantity) || 0)
      : (parseFloat(item.quantity) || 1);
    return sum + qty * (parseFloat(item.unit_price) || 0);
  }, 0);
  const taxRate = formData.tax_rate === '' ? 0 : (parseFloat(formData.tax_rate) || 0);
  const discountAmount = formData.discount_amount === '' ? 0 : (parseFloat(formData.discount_amount) || 0);
  const taxAmount = (subtotal * taxRate) / 100;
  const totalAmount = subtotal + taxAmount - discountAmount;
  const selectedPatient = lockedPatient
    || patients.find((p) => String(p.id) === String(formData.patient_id))
    || null;
  const selectedAssessment = lockedAssessment
    || assessments.find((a) => String(a.id) === String(formData.assessment_id))
    || null;

  const formatAssessmentOptionLabel = (assessment) => {
    const date = formatCalendarDateDMY(assessment.assessment_date);
    const parts = [];
    if (assessment.patient_name) parts.push(`Patient: ${assessment.patient_name}`);
    if (assessment.lead_name) parts.push(`Lead: ${assessment.lead_name}`);
    const namePart = parts.length > 0 ? ` - ${parts.join(' / ')}` : '';
    return `${date}${namePart}`;
  };

  return (
    <div className="modal-shell flex items-center justify-center" onClick={onClose}>
      <div className="modal-panel max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">
            {invoice ? 'Edit Invoice' : 'Create New Invoice'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-5 sm:space-y-6 overflow-y-auto">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

          {!lockedPatient && !lockedAssessment && (
          <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Category <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="invoice_category"
                    value="assessment"
                    checked={formData.invoice_category === 'assessment'}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-gray-700">Assessment Fees</span>
                </label>
                <label className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="invoice_category"
                    value="patient"
                    checked={formData.invoice_category === 'patient'}
                    onChange={handleChange}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500 focus:ring-2"
                  />
                  <span className="ml-2 text-sm text-gray-700">Patient Fees</span>
                </label>
              
              </div>
            </div>
          )}

          {lockedAssessment && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Assessment</label>
              <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
                {formatAssessmentOptionLabel(selectedAssessment || lockedAssessment)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Assessment invoice — not linked to a patient record</p>
            </div>
          )}

            {formData.invoice_category === 'patient' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Patient <span className="text-red-500">*</span>
                </label>
                {lockedPatient ? (
                  <div className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
                    {selectedPatient?.full_name || lockedPatient.full_name}
                    {(selectedPatient?.patient_code || lockedPatient.patient_code)
                      ? ` (${selectedPatient?.patient_code || lockedPatient.patient_code})`
                      : ''}
                  </div>
                ) : (
                  <select
                    name="patient_id"
                    value={formData.patient_id}
                    onChange={handleChange}
                    required={formData.invoice_category === 'patient'}
                    disabled={!!invoice}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                  >
                    <option value="">Select Patient</option>
                    {patients.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.full_name} ({p.patient_code})
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

{formData.invoice_category === 'assessment' && !lockedAssessment && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assessment <span className="text-red-500">*</span>
                </label>
                <select
                  name="assessment_id"
                  value={formData.assessment_id}
                  onChange={handleChange}
                  required={formData.invoice_category === 'assessment'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select Assessment</option>
                  {assessments.map((assessment) => (
                    <option key={assessment.id} value={assessment.id}>
                      {formatAssessmentOptionLabel(assessment)}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">Assessment invoices are linked to assessments only, not patients</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                name="due_date"
                value={formData.due_date}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

        

            

            {formData.invoice_category === 'patient' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="payment_type"
                  value={formData.payment_type}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="therapy">Therapy</option>
                  <option value="training">Training</option>
                  <option value="event">Event</option>
                  <option value="seminar">Seminar</option>
                  <option value="consultation">Consultation</option>
                  <option value="other">Other</option>
                </select>
              </div>
            )}

            {invoice && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            )}
          </div>

          {/* Invoice Items */}
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-semibold text-gray-700">Invoice Items</h4>
              <button
                type="button"
                onClick={addItem}
                className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 flex items-center gap-1"
              >
                <HiPlus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            <div className="space-y-3">
              {formData.items.map((item, index) => (
                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                  <div className="sm:col-span-1 flex items-center gap-2">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <span className="text-xs text-gray-600">QTY:</span>
                      <button
                        type="button"
                        onClick={() => toggleItemQuantity(index)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          item.showQuantity ? 'bg-primary-600' : 'bg-gray-300'
                        }`}
                        title="Toggle Quantity"
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            item.showQuantity ? 'translate-x-4' : 'translate-x-0.5'
                          }`}
                          style={{ marginTop: '2px' }}
                        />
                      </button>
                    </label>
                  </div>
                  <div className={item.showQuantity ? 'sm:col-span-4' : 'sm:col-span-5'}>
                    {(() => {
                      const inCatalog = invoiceItemDescriptions.some(
                        (desc) => desc.description === item.description
                      );
                      if (item.description && !inCatalog) {
                        return (
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            required
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg bg-gray-50"
                            title="Description not in catalog — edit as text"
                          />
                        );
                      }
                      return (
                        <select
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          required
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                        >
                          <option value="">Select Description</option>
                          {item.description && !inCatalog ? (
                            <option value={item.description}>{item.description}</option>
                          ) : null}
                          {invoiceItemDescriptions.map((desc) => (
                            <option key={desc.id} value={desc.description}>
                              {desc.description} {desc.default_price ? `(₹${parseFloat(desc.default_price).toFixed(2)})` : ''}
                            </option>
                          ))}
                        </select>
                      );
                    })()}
                  </div>
                  {item.showQuantity && (
                    <div className="sm:col-span-2">
                      <input
                        type="number"
                        value={item.quantity === '' || item.quantity === 0 ? '' : item.quantity}
                        onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="Qty"
                        min="0.01"
                        step="0.01"
                        required
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}
                  <div className="sm:col-span-3">
                    <input
                      type="number"
                      value={item.unit_price === '' || item.unit_price === 0 ? '' : item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)}
                      placeholder="Unit Price"
                      min="0"
                      step="0.01"
                      required
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="sm:col-span-1 text-sm font-medium text-gray-700 flex items-center">
                    ₹{(
                      (item.showQuantity ? (parseFloat(item.quantity) || 0) : (parseFloat(item.quantity) || 1))
                      * (parseFloat(item.unit_price) || 0)
                    ).toFixed(2)}
                  </div>
                  <div className="sm:col-span-1 flex items-center justify-end gap-1">
                    {formData.items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:text-red-700 p-1"
                        title="Delete Item"
                      >
                        <HiTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tax Rate (%)
                </label>
                <input
                  type="number"
                  name="tax_rate"
                  value={formData.tax_rate === '' || formData.tax_rate === 0 ? '' : formData.tax_rate}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount Amount (₹)
                </label>
                <input
                  type="number"
                  name="discount_amount"
                  value={formData.discount_amount === '' || formData.discount_amount === 0 ? '' : formData.discount_amount}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₹{subtotal.toFixed(2)}</span>
                </div>
                {taxAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax ({formData.tax_rate}%):</span>
                    <span className="font-medium">₹{taxAmount.toFixed(2)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Discount:</span>
                    <span className="font-medium text-red-600">-₹{discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-300">
                  <span className="font-semibold text-gray-900">Total Amount:</span>
                  <span className="font-bold text-lg text-primary-600">₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Additional notes for the invoice..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2.5 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto px-4 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : invoice ? 'Update Invoice' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InvoiceForm;

